import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const Counter = ({ duration, start, end, step, decimals }) => {
  const ref = useRef();

  const [visible, setVisible] = useState(false);

  const [value, setValue] = useState(start || 0);

  const [completed, setCompleted] = useState(false);

  const createObserver = useCallback(
    () =>
      new IntersectionObserver(([entry]) => {
        setVisible(entry.isIntersecting);
      }),
    [],
  );

  const formatNumber = useCallback((n) => Number(n).toFixed(decimals), [
    decimals,
  ]);

  const shouldStop = useCallback(
    () => formatNumber(value) === formatNumber(end),
    [formatNumber, value, end, decimals],
  );

  const increment = useCallback(() => {
    setValue((state) => (shouldStop() ? state : state + step));
  }, [shouldStop, step]);

  useEffect(() => {
    const observer = createObserver();

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.unobserve(ref.current);
    };
  }, [ref, createObserver]);

  useEffect(() => {
    const timeout = (duration * 1000) / end;

    let interval;

    if (visible && !completed) {
      interval = setInterval(increment, timeout);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [visible, completed, duration, end, increment]);

  useEffect(() => {
    setCompleted(shouldStop());
  }, [shouldStop]);

  return (
    <span ref={ref}>{visible ? formatNumber(value) : formatNumber(start)}</span>
  );
};

Counter.propTypes = {
  duration: PropTypes.number,
  start: PropTypes.number.isRequired,
  end: PropTypes.number.isRequired,
  step: PropTypes.number,
  decimals: PropTypes.number,
};

Counter.defaultProps = {
  duration: 1.5,
  step: 1,
  decimals: 0,
};

export default Counter;
